/**
 * Custom task node component for React Flow
 */

import { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { StepCardinality } from '../../types/pipeline';
import './TaskNode.css';

interface TaskNodeData {
  taskType: string;
  label: string;
  config: Record<string, any>;
  status?: 'pending' | 'running' | 'completed' | 'failed';
  agentId?: string;
  agentVersion?: number;
  agentName?: string;
  modelName?: string;
  contextFieldCount?: number;
}

interface TaskNodeProps {
  id: string;
  data: TaskNodeData;
  selected?: boolean;
}

// Icon reflects the node's task_type category - data source inputs, item search,
// and regular (agent-driven or built-in) task steps each get a distinct glyph
// instead of the previous meaningless filled square.
const NodeTypeIcon = ({ taskType }: { taskType: string }) => {
  if (taskType.startsWith('data_source_')) {
    return (
      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <ellipse cx="8" cy="4" rx="5" ry="2" />
        <path d="M3 4v8c0 1.1 2.2 2 5 2s5-.9 5-2V4" />
        <path d="M3 8c0 1.1 2.2 2 5 2s5-.9 5-2" />
      </svg>
    );
  }
  if (taskType === 'item_search') {
    return (
      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="7" cy="7" r="4.5" />
        <path d="M13.5 13.5L10.5 10.5" />
      </svg>
    );
  }
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4.5" y="4.5" width="7" height="7" rx="1.2" />
      <path d="M8 1.5V4.5M8 11.5v2.5M1.5 8H4.5M11.5 8H14.5" />
    </svg>
  );
};

const AgentIcon = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="currentColor">
    <path d="M8 1.5l1.2 3.8L13 6.5l-3.8 1.2L8 11.5l-1.2-3.8L3 6.5l3.8-1.2L8 1.5z" />
  </svg>
);

// Tag/field glyph for the "N context fields" badge - i.e. how many
// variables/fields this node's agent uses in its prompts.
const FieldsIcon = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 3.5h6l6 6-4.5 4.5-6-6v-4.5z" />
    <circle cx="5.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);

// Loop arrow - this node (or the items it declares) fans out one-at-a-time.
export const PerItemIcon = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 8a5 5 0 0 1 8.5-3.5L13 6" />
    <path d="M13 3v3h-3" />
    <path d="M13 8a5 5 0 0 1-8.5 3.5L3 10" />
    <path d="M3 13v-3h3" />
  </svg>
);

// Stacked layers - this node runs once over the whole batch at once.
export const AggregateIcon = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 2L14 5.2L8 8.4L2 5.2L8 2Z" />
    <path d="M2 8.4L8 11.6L14 8.4" />
    <path d="M2 11.6L8 14.8L14 11.6" />
  </svg>
);

// Branching fan-out - one instance per (item, related-item) pair from an upstream item_search node.
const PerRelatedItemIcon = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="3" cy="8" r="1.6" fill="currentColor" stroke="none" />
    <path d="M4.5 8H7M7 8L11 3.5M7 8L11 12.5" />
    <circle cx="12.5" cy="3.5" r="1.6" fill="currentColor" stroke="none" />
    <circle cx="12.5" cy="12.5" r="1.6" fill="currentColor" stroke="none" />
  </svg>
);

const CARDINALITY_BADGE: Record<string, { icon: JSX.Element; label: string; title: string }> = {
  [StepCardinality.PER_ITEM]: {
    icon: <PerItemIcon />,
    label: 'per item',
    title: 'Loops over every item in scope, one instance each',
  },
  [StepCardinality.AGGREGATE]: {
    icon: <AggregateIcon />,
    label: 'once over all',
    title: 'Runs once over all items together',
  },
  [StepCardinality.PER_RELATED_ITEM]: {
    icon: <PerRelatedItemIcon />,
    label: 'per related item',
    title: 'One instance per (item, related item) pair from an upstream search',
  },
};

// One-line summary of an output node's configuration, so a misconfigured node is
// visible on the canvas without opening its sidebar.
function outputNodeSummary(taskType: string, config: Record<string, any> | undefined): string | null {
  if (taskType === 'create_item') {
    const mappings: number = config?.field_mappings?.length ?? 0;
    const target = config?.target_type_id ? `tracker ${config.target_type_id}` : 'no target tracker';
    return `${target} · ${mappings} field${mappings !== 1 ? 's' : ''}`;
  }
  if (taskType === 'pushback' && config?.loop) {
    const items = config.loop.items_binding;
    const list = items?.upstream_step_reference && items?.output_field
      ? `${items.upstream_step_reference}.${items.output_field}`
      : 'no list';
    return `↻ each item of ${list}`;
  }
  if (taskType === 'pushback') {
    const binding = config?.target_field_binding;
    const target = binding
      ? binding.source === 'step_output'
        ? `${binding.upstream_step_reference}.${binding.output_field}`
        : binding.source_name || binding.item_field
      : config?.target_field;
    return target ? `${config?.operation || 'OVERRIDE'} → ${target}` : 'no target field';
  }
  if (taskType === 'create_reference') {
    return config?.reference_type || 'REFERENCE';
  }
  return null;
}

export const TaskNode = memo<TaskNodeProps>(({ data, selected }) => {
  // Data source nodes stash bookkeeping (name/required) in `config` purely so
  // pipelineToWorkflow can round-trip them into a WorkflowInput on save - see
  // client.ts's workflowToPipeline. It's not user-facing configuration (there's
  // no UI to edit it, see DataSourceConfig.tsx), so it shouldn't show a "config" count.
  const isDataSource = data.taskType.startsWith('data_source_');

  // Mirrors NodeConfigSidebar.tsx's default - an unset config.cardinality isn't "no
  // cardinality", it's whatever the schema defaults to. Data sources have none of their
  // own: how a step consumes one is shown on the connecting edge (MovableEdge.tsx).
  const cardinality: string = data.config?.cardinality || StepCardinality.PER_ITEM;
  const cardinalityBadge = isDataSource ? undefined : CARDINALITY_BADGE[cardinality];
  const outputSummary = outputNodeSummary(data.taskType, data.config);

  return (
    <div className={`task-node ${selected ? 'selected' : ''} ${data.status || 'pending'}`}>
      {data.status && data.status !== 'pending' && (
        <div className={`node-status-badge status-${data.status}`}>
          {data.status === 'running' && (
            <svg className="node-status-badge-spinner" width="13" height="13" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" fill="none" strokeDasharray="40" strokeDashoffset="10" strokeLinecap="round" />
            </svg>
          )}
          {data.status === 'completed' && (
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
              <path d="M3 8.5L6.5 12L13 4" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
          {data.status === 'failed' && (
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
              <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
            </svg>
          )}
        </div>
      )}
      <Handle type="target" position={Position.Top} className="task-node-handle" />

      <div className="task-node-header">
        <div className="task-node-icon">
          <NodeTypeIcon taskType={data.taskType} />
        </div>
        <div className="task-node-title">{data.label}</div>
      </div>

      <div className="task-node-body">
        <div className="task-node-type">{data.taskType}</div>
        {outputSummary && <div className="task-node-output-summary">{outputSummary}</div>}
        {data.agentId && data.agentName && (
          <div
            className="task-node-agent"
            title={data.modelName ? `Agent: ${data.agentName} · Model: ${data.modelName}` : `Agent: ${data.agentName}`}
          >
            <AgentIcon />
            <span className="task-node-agent-name">{data.agentName}</span>
            {data.modelName && <span className="task-node-agent-model">{data.modelName}</span>}
          </div>
        )}
        <div className="task-node-chips">
          {cardinalityBadge && (
            <div className={`task-node-cardinality cardinality-${cardinality}`} title={cardinalityBadge.title}>
              {cardinalityBadge.icon}
              {cardinalityBadge.label}
            </div>
          )}
          {!!data.contextFieldCount && (
            <div
              className="task-node-fields"
              title={`Uses ${data.contextFieldCount} context field${data.contextFieldCount !== 1 ? 's' : ''}`}
            >
              <FieldsIcon />
              {data.contextFieldCount} field{data.contextFieldCount !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      </div>

      <Handle type="source" position={Position.Bottom} className="task-node-handle" />
    </div>
  );
});

TaskNode.displayName = 'TaskNode';
